//Formatters
import { format } from 'svelte-i18n';
//Helpers
import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';
import {
	getTemplateComponents,
	getTemplateMode,
	getTemplateService,
	isXT1ComponentDevice,
	isXT1Module,
} from './template_helpers';
//Types
import type { ChartConfig } from 'src/types/Chart';
import type { Device } from 'src/types/Device';
import type { GetStatsResponseData } from 'src/types/requests/charts/get_stats_period';
import type { TemplateComponent, TemplateService } from 'src/types/VirtualComponents';
import type { SelectOptionType } from '@Shelly/shelly-design-system/index';

type temp_unit = 'C' | 'F';
type pressure_unit = 'PSI' | 'kPa' | 'bar';
type volume_unit = 'gal' | 'lit' | 'm3' | 'ft3';

const KPA_TO_PSI = 0.145;
const KPA_TO_BAR = 0.01;

const M3_TO_US_GAL = 264.172;
const M3_TO_LIT = 1000;
const M3_TO_FT3 = 35.3147;

export const getVolumeUnitOptions = (): SelectOptionType[] => {
	const $format = get(format);
	return [
		{
			title: $format('title_unit_gal', { default: 'Gallons' }),
			value: 'gal',
		},
		{
			title: $format('title_unit_lit', { default: 'Liters' }),
			value: 'lit',
		},
		{
			title: 'm³',
			value: 'm3',
		},
		{
			title: 'ft³',
			value: 'ft3',
		},
	];
};

export const getPresureUnitOptions = (): SelectOptionType[] => {
	return [
		{
			title: 'PSI',
			value: 'PSI',
		},
		{
			title: 'kPa',
			value: 'kPa',
		},
		{
			title: 'bar',
			value: 'bar',
		},
	];
};

const roundNumber = (value: number, precision: number): number => {
	return Number(value.toFixed(precision));
};

const getTemperature = (value: number, unit: temp_unit): number => {
	if (unit === 'F') {
		const f_value = (value * 9) / 5 + 32;
		return roundNumber(f_value, 2);
	}
	return roundNumber(value, 2);
};

const getPressureValue = (value: number, unit: pressure_unit): number => {
	switch (unit) {
		case 'PSI':
			return roundNumber(value * KPA_TO_PSI, 2);
		case 'kPa':
			return roundNumber(value, 2);
		case 'bar':
			return roundNumber(value * KPA_TO_BAR, 2);
		default:
			console.error('Invalid pressure unit', unit);
			break;
	}
	return 0;
};

const getFlowRateValue = (value: number, unit: volume_unit): number => {
	switch (unit) {
		case 'gal':
			return roundNumber(value * M3_TO_US_GAL, 2);
		case 'lit':
			return roundNumber(value * M3_TO_LIT, 4);
		case 'm3':
			return roundNumber(value, 4);
		case 'ft3':
			return roundNumber(value * M3_TO_FT3, 2);
		default:
			console.error('Invalid flow rate unit', unit);
			break;
	}
	return 0;
};

const getConsumptionValue = (value: number, unit: volume_unit): number => {
	switch (unit) {
		case 'gal':
			return roundNumber(value * M3_TO_US_GAL, 3);
		case 'lit':
			return roundNumber(value * M3_TO_LIT, 3);
		case 'm3':
			return roundNumber(value, 3);
		case 'ft3':
			return roundNumber(value * M3_TO_FT3, 3);
		default:
			console.error('Invalid consumption unit', unit);
			break;
	}
	return 0;
};

export const getUnitByRole = (
	role: string,
	templateService: TemplateService,
	isFormat: boolean = false,
): string => {
	let unit = '';
	if (/^(water_pressure)$/.test(role)) {
		unit = templateService.settings.pressure_unit;
	} else if (/^(flow_rate)$/.test(role)) {
		unit = templateService.settings.volume_unit;
		if (isFormat) unit += '/min';
	} else if (/^(water_temperature)$/.test(role)) {
		unit = templateService.settings.temp_unit;
		if (isFormat) unit = `°${unit}`;
	} else if (/^(water_consumption)$/.test(role)) {
		unit = templateService.settings.volume_unit;
	}
	return isFormat ? unit.replace('3', '³').replace(/^lit/, 'L') : unit;
};

export const convertUnits = (value: number, role: string, unit: string): number => {
	if (/^(water_pressure)$/.test(role)) {
		value = getPressureValue(value, unit as pressure_unit);
	} else if (/^(flow_rate)$/.test(role)) {
		value = getFlowRateValue(value, unit as volume_unit);
	} else if (/^(water_temperature)$/.test(role)) {
		value = getTemperature(value, unit as temp_unit);
	} else if (/^(water_consumption)$/.test(role)) {
		value = getConsumptionValue(value, unit as volume_unit);
	}
	return value;
};

export const convertNeoValveChartUnits = (
	data: GetStatsResponseData,
	chartConfig: ChartConfig,
): void => {
	const device: Device = get(devices)[chartConfig.deviceID];
	if (getTemplateMode(device) == 'neo-water-valve-advanced' && Array.isArray(data?.history)) {
		const templateService: TemplateService = getTemplateService(device);
		const role: string = templateService.components.find(
			(i) =>
				i?.key == `number:${chartConfig?.deviceChannel}` ||
				i?.key == `object:${Number(chartConfig?.deviceChannel) - 3000}`,
		)?.status?._attrs?.role;
		if (role) {
			const unit = getUnitByRole(role, templateService);
			data.history = data.history.map((i) => {
				if (i?.missing || i?.['value_converted'] === true) return i;
				if (/^(water_consumption)$/.test(role)) {
					return {
						...i,
						value: convertUnits(i.value, role, unit),
						value_converted: true,
					};
				}
				return {
					...i,
					min: convertUnits(i.min, role, unit),
					max: convertUnits(i.max, role, unit),
					avg: convertUnits(i.avg, role, unit),
					value_converted: true,
				};
			});
			chartConfig.units = getUnitByRole(role, templateService, true);
		}
	}
};

export const getSubmitConsumptionValue = (data: number[], unit: string) => {
	return data.map((value) => {
		switch (unit) {
			case 'gal':
				return roundNumber(value / M3_TO_US_GAL, 3);
			case 'lit':
				return roundNumber(value / M3_TO_LIT, 3);
			case 'm3':
				return roundNumber(value, 3);
			case 'ft3':
				return roundNumber(value / M3_TO_FT3, 3);
			default:
				console.error('Invalid consumption unit', unit);
				break;
		}
		return 0;
	});
};

export const getSubmitPressureValue = (data: number[], unit: string) => {
	return data.map((value) => {
		switch (unit) {
			case 'PSI':
				return roundNumber(value / KPA_TO_PSI, 2);
			case 'kPa':
				return roundNumber(value, 2);
			case 'bar':
				return roundNumber(value / KPA_TO_BAR, 2);
			default:
				console.error('Invalid pressure unit', unit);
				break;
		}
		return 0;
	});
};

/**
 * The device only support °C unit
 */
export function getSubmitTemperature(value: number[], temp_unit: string) {
	return value.map((value) => {
		if (temp_unit === 'F') {
			const f_value = ((value - 32) * 5) / 9;
			return roundNumber(f_value, 2);
		}
		return roundNumber(value, 2);
	});
}

export const getTemplateTargetTemp = (device: Device): TemplateComponent | null => {
	return isXT1Module(device?.type) && !isXT1ComponentDevice(device)
		? getTemplateComponents(device, /^number:/, /^target_temperature$/)?.[0]
		: null;
};

export const getTemplateTempUnit = (device: Device): 'C' | 'F' => {
	const templateService: TemplateService = getTemplateService(device);
	const targetTemperature = getTemplateTargetTemp(device);
	const unit = String(
		templateService?.settings?.temp_unit || targetTemperature?.config?.meta?.ui?.unit || 'C',
	).toUpperCase();
	return unit.includes('F') ? 'F' : 'C';
};
